<template>
	<div class="file-manager-container overflow-auto">
		<h1 class="heading">
			{{ $t('file-manager.title') }}
		</h1>
		<b-button v-b-modal.file-upload>
			{{ $t('file-manager.add-new') }}
		</b-button>
		<b-modal
			id="file-upload"
			:title="$t('file-manager.add-new-file')"
			:ok-title="$t('file-manager.continue')"
			@ok="uploadSelectedFile()"
			@hidden="uploadFileName = $t('file-manager.no-file-selected')"
		>
			<div class="file-upload">
				<i class="tan-icon-arrow-up" />
				<div class="mt-5">
					{{ $t('file-manager.drag-and-drop') }}
				</div>
				<div class="mt-3">
					{{ $t('file-manager.or') }}
				</div>
				<button class="browse-files" @click="clickInput">
					{{ $t('file-manager.browse-files') }}
				</button>
				<div class="selected-file">
					{{ uploadFileName }}
				</div>
				<input
					ref="fileInput"
					type="file"
					hidden
					@change="prepareFileUpload($event)"
				>
			</div>
		</b-modal>
		<div class="toolbar">
			<div class="toolbar-section">
				<!--
				<label>
					<input
						v-model="view"
						class="toolbar-radio"
						type="radio"
						value="list"
					>
					<b-icon-list-ul />
				</label>
				<label>
					<input
						v-model="view"
						class="toolbar-radio"
						type="radio"
						value="grid"
					>
					<b-icon-grid-fill />
				</label>
				-->
				<b-form-select v-model="filterTag" class="toolbar-select" :options="tagOptions" />
				<b-form-select v-model="filterFileType" class="toolbar-select" :options="fileTypeOptions" />
				<b-button :disabled="!(filterFileType || filterTag)" @click="clearFilters">
					{{ $t('file-manager.clear-filter') }}
				</b-button>
			</div>
			<div class="toolbar-section">
				<label v-if="view !== 'list'"> 
					<input
						v-model="sort"
						class="toolbar-radio"
						type="radio"
						value="alphabetical"
					>
					<b-icon-sort-alpha-down />
				</label>
				<label v-if="view !== 'list'">
					<input
						v-model="sort"
						class="toolbar-radio"
						type="radio"
						value="recent"
					>
					<b-icon-sort-down />
				</label>
				<search-bar v-model="filterString" debounce="300" />
			</div>
		</div>
		<b-table
			v-if="view === 'list'"
			striped
			outlined
			:fields="fileFields"
			:filter="filterFields"
			:filter-function="filterFiles"
			:items="filesInView"
			primary-key="id"
		>
			<template #cell(id)="data">
				<!-- This field is not displayed but used to provide a unique identifier to the rows -->
				{{ data.item.id }}
			</template>
			<template #cell(icon)="data">
				<div v-if="data.item.type.startsWith('image/')" :id="data.item.fileUri" class="file-preview-thumbnail">
					<UserFile :file-uri="data.item.fileUri" :static="true" />
				</div>
				<div v-else>
					<b-icon-file-earmark scale="2" />
				</div>
			</template>
			<template #cell(name)="data">
				{{ data.item.name }}<br>
				<span class="small text-muted">{{ data.item.type }}</span>
			</template>
			<template #cell(description)="data">
				<div v-if="data.item.newDescription">
					<b-input-group>
						<b-form-input
							v-model="data.item.newDescription"
							class="form-control"
							@keyup.enter="updateDescription(data.item.id, data.item.newDescription)"
						/>
						<b-input-group-append>
							<b-button variant="primary" @click="updateDescription(data.item.id, data.item.newDescription)">
								<b-icon-check />
							</b-button>
							<b-button variant="secondary" @click="editDescription(data.item.id, false)">
								<b-icon-x />
							</b-button>
						</b-input-group-append>
					</b-input-group>
				</div>
				<div v-else>
					{{ data.item.description }}
					<b-icon
						v-if="data.item.user.id === $store.state.user.id"
						icon="pencil-square"
						@click="editDescription(data.item.id, data.item.description)"
					/>
				</div>
			</template>
			<template #cell(size)="data">
				{{ filesize(data.item.size) }}
			</template>
			<template #cell(tags)="data">
				<!-- <b-form-tags v-model="data.item.tags" :disabled="data.item.user.id !== $store.state.user.id" placeholder="" /> -->
				<span v-if="data.item.tags.length > 0">
					<span v-for="tag of data.item.tags" :key="tag">
						<!-- :disabled="tag.startsWith('dmp:') || data.item.user.id !== $store.state.user.id" -->
						<b-form-tag :disabled="data.item.user.id !== $store.state.user.id" @remove="removeTag(data.item.id, tag)">
							<b-icon-link45deg v-if="tag.startsWith('dmp:')" title="System Link" /><b-icon-tag v-else title="User Defined Tag" />
							{{ tag }}
						</b-form-tag>
					</span>
				</span>
				<b-input-group v-if="data.item.user.id === $store.state.user.id || ['OWNER', 'ADMIN'].includes($store.state.user.currentEnterprise.enterprise_user.role)" class="mb-2">
					<b-form-input
						v-model="data.item.newTags"
						placeholder="Add a new tag... "
						class="form-control"
						@keyup.enter="addNewTag(data.item.id, data.item.newTags)"
					/>
					<b-input-group-append>
						<b-button variant="primary" @click="addNewTag(data.item.id, data.item.newTags)">
							<b-iconstack>
								<b-icon
									stacked
									icon="tag"
									scale="1.5"
									shift-v="-1"
								/>
								<b-icon
									stacked
									icon="plus"
									scale="1.25"
									shift-h="8"
									shift-v="7"
								/>
							</b-iconstack>
						</b-button>
					</b-input-group-append>
				</b-input-group>
			</template>
			<template #cell(user)="data">
				{{ data.item.user.lastName }}, {{ data.item.user.firstName }}<br>
				<span class="small text-muted">{{ data.item.user.email }}</span>
			</template>
			<template #cell(createdAt)="data">
				{{ formatDateTime(data.item.createdAt) }}
			</template>
			<template #cell(actions)="data">
				<div v-if="data.item.user.id === $store.state.user.id || ['OWNER', 'ADMIN'].includes($store.state.user.currentEnterprise.enterprise_user.role)">
					<b-button variant="danger" :disabled="isSystemLinkPresent(data.item.tags)" @click="deleteFile(data.item.id)">
						<b-icon-x />
					</b-button>
				</div>
			</template>
		</b-table>
		<div v-else class="grid" />
	</div>
</template>

<script>
import filesize from 'filesize';
import { DateTime } from 'luxon';
import UserFile from '@/components/Files/UserFile.vue';

export default {
	components: {
		UserFile,
	},
	data() {
		return {
			file: '',
			uploadFileName: this.$t('file-manager.no-file-selected'),
			filterFileType: null,
			filterString: '',
			filterTag: null,
			sort: 'alphabetical',
			view: 'list',
			filesInView: [],
			fileFields: [
				{ key: 'icon', label: '' },
				{ key: 'name', label: 'File', sortable: true },
				/* { key: 'description', label: 'Description' }, -- Hiding the description field. */
				{ key: 'size', label: 'Size', sortable: true },
				{ key: 'tags', label: 'Tags' },
				{ key: 'user', label: 'Uploaded By', sortable: true },
				{ key: 'createdAt', label: 'Uploaded Date', sortable: true },
				{ key: 'actions', label: 'Actions' },
			],
			fileTypesInView: [],
			tagsInView: [],
		};
	},
	computed: {
		fileTypeOptions() {
			return [
				{ text: this.$t('file-manager.all-file-types'), value: null },
				...this.fileTypesInView.map((x) => {
					return { text: x, value: x };
				}),
			];
		},
		tagOptions() {
			return [
				{ text: this.$t('file-manager.all-tags'), value: null },
				...this.tagsInView.map((x) => {
					return { text: x, value: x };
				}),
			];
		},
		filterFields() {
			const tag = this.filterTag;
			const type = this.filterFileType;
			return {
				tag,
				type,
			};
		},
	},
	watch: {
		filterString: function searchChanged(newVal, oldVal) {
			if (newVal === '' || newVal === null) {
				this.getFiles();
			} else if (newVal.length >= 3 && newVal !== oldVal) {
				this.getFiles(newVal);
			}
		},
	},
	created() {
		this.getFiles();
	},
	methods: {
		filesize,
		formatDateTime(value) {
			return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);
		},
		clickInput() {
			this.$refs.fileInput.click();
		},
		prepareFileUpload(event) {
			[this.file] = event.target.files;
			this.uploadFileName = this.file.name;

			const fileReader = new FileReader();
			const that = this;
			fileReader.onload = function fileReaderOnload(e) {
				crypto.subtle
					.digest('SHA-256', e.target.result)
					.then((hashBuffer) => {
						const hashArray = Array.from(new Uint8Array(hashBuffer));
						that.fileHash = hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('');
						that.checkFileHash(that.fileHash);
					})
					.catch((error) => {
						console.error(error);
						this.fileHash = null;
					});
			};
			fileReader.readAsArrayBuffer(this.file);
		},
		checkFileHash(fileHash) {
			// Call /files/hash to determine if file is previously uploaded
			// If 204 (No Content) there is no matching file, so upload
			// If 200, the metadata of the matching file is returned, so continue as if you uploaded
			this.axios.get(`/files/hash/${fileHash}`).then((response) => {
				if (response.status === 200) {
					// 200: match; 204: no match
					// this file has already been uploaded.
					// we need a workflow as to how the user should be notified
					// for now, close modal and pop a toast
					this.$bvModal.hide('file-upload');
					this.file = null;
					this.uploadFileName = this.$t('file-manager.no-file-selected');
					this.$bvToast.toast(this.$t('file-manager.matching-hash-description'), {
						title: this.$t('file-manager.matching-hash-title'),
						variant: 'warning',
					});
				}
			});
		},
		uploadSelectedFile() {
			// upload file
			const formData = new FormData();
			formData.append('file', this.file);

			this.axios
				.put(`/files`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				})
				.then((data) => {
					data.data.user = { ...this.$store.state.user };
					data.data.fileUri = `dmp:file:${data.data.id}`;
					this.filesInView.push(data.data);
					this.file = null;
					this.uploadFileName = this.$t('file-manager.no-file-selected');
					this.$bvToast.toast(this.$t('file-manager.uploaded-description'), {
						title: this.$t('file-manager.uploaded-title'),
						variant: 'success',
					});
				})
				.catch((error) => {
					console.error(error);
				});
		},
		getFiles(searchValue = null) {
			let url = '/files/enterprise';
			const qp = new URLSearchParams();
			if (searchValue) qp.set('name', searchValue);
			url = `${url}?${qp.toString()}`;

			this.axios.get(url).then((response) => {
				this.filesInView = [];
				for (const item of response.data) {
					item.fileUri = `dmp:file:${item.id}`;
					this.filesInView.push(item);
					this.fileTypesInView = [...new Set(this.fileTypesInView.concat([item.type]))].sort();
					this.tagsInView = [...new Set(this.tagsInView.concat(item.tags))].sort();
				}
			});
		},
		getUpdateFile(o) {
			if (!(o || o.id)) return false;
			// eslint-disable-next-line guard-for-in
			for (const i in this.filesInView) {
				const file = this.filesInView[i];
				if (file.id !== o.id) continue;
				for (const key of Object.keys(o)) {
					if (key === 'id') continue;
					file[key] = o[key];
				}
				this.$set(this.filesInView, i, file); // https://v2.vuejs.org/v2/guide/reactivity.html?redirect=true#For-Arrays
				return file;
			}
			return false;
		},
		removeFile(id) {
			let arrayI = null;
			for (const i in this.filesInView) {
				if (this.filesInView[i].id === id) {
					arrayI = i;
					break;
				}
			}
			if (arrayI) this.filesInView.splice(arrayI, 1);
		},
		isSystemLinkPresent(tags) {
			if (!tags || !Array.isArray(tags)) return false;
			for (const tag of tags) {
				if (tag.startsWith('dmp')) return true;
			}
			return false;
		},
		addNewTag(id, tag) {
			const selectedFile = this.getUpdateFile({ id, newTags: null });
			/* 			for (const file of this.filesInView) {
				if (file.id !== id) continue;
				selectedFile = file;
				file.newTags = null;
				break;
			}  */
			if (!selectedFile.tags.includes(tag)) {
				this.axios
					.post(`/files/${id}/tags`, [tag])
					.then((response) => {
						this.getUpdateFile({ id, tags: response.data });
					})
					.catch((error) => {
						console.error(error);
					});
			}
		},
		removeTag(id, tag, confirm = null) {
			const selectedFile = this.getUpdateFile({ id });
			// allow a user to remove a DMP URI, but confirm first.
			if (tag.startsWith('dmp:')) {
				if (!confirm) {
					const modalOptionsObject = {
						message: this.$t('file-manager.remove-dmp-link'),
					};
					this.$msgBoxConfirm(modalOptionsObject).then((value) => {
						if (value) this.removeTag(id, tag, true);
					});
					return false;
				}
			}

			if (selectedFile.tags) {
				selectedFile.tags = selectedFile.tags.filter((item) => item !== tag);
				this.axios
					.put(`/files/${id}/tags`, selectedFile.tags)
					.then((response) => {
						this.getUpdateFile({ id, tags: response.data });
					})
					.catch((error) => {
						console.error(error);
					});
			}
			return true;
		},
		editDescription(id, newDescription) {
			if (newDescription === null || newDescription === '') {
				newDescription = ' '; // space string prompts switching to field
			} else if (newDescription === false) {
				newDescription = null; // false argument cancels the text field
			} else {
				newDescription = newDescription.trim();
			}
			this.getUpdateFile({ id, newDescription });
		},
		updateDescription(id, description) {
			this.getUpdateFile({ id, newDescription: null });
			if (description === '') description = ' '; // express treats an empty string as null and drops it from the body properties.
			this.axios
				.patch(`/files/${id}/metadata`, { description })
				.then((response) => {
					this.getUpdateFile({ ...response.data });
				})
				.catch((error) => {
					console.error(error);
				});
		},
		deleteFile(id) {
			this.axios
				.delete(`/files/${id}`)
				.then((response) => {
					if (response.status === 204) {
						this.removeFile(id);
						this.$bvToast.toast(this.$t('file-manager.action-deleted'), {
							title: this.$t('appbar.labels.success'),
							variant: 'success',
						});
					}
				})
				.catch((error) => {
					console.error(error);
				});
		},
		filterFiles(row, props) {
			if (props.type === null && props.tag === null) return true;
			if (props.type && props.tag) {
				if (props.type === row.type && row.tags.indexOf(props.tag) >= 0) return true;
				return false;
			}
			if (props.type && props.type === row.type) return true;
			if (props.tag && row.tags.indexOf(props.tag) >= 0) return true;
			return false;
		},
		clearFilters() {
			this.filterFileType = this.filterTag = null;
		},
	},
};
/* Notes on Search/Filtering
Currently, the value of the search bar is passed to the backend to retrieve a new list of files. 
The Tag and Type dropdowns are populated from the list of files loaded. 
A search by name will pull up files in the initial load, as well as other matching records in the database.
A tag or type filter will only pull up values from the list of files previously loaded. 
*/
</script>

<style scoped>
.file-manager-container {
	background: #eeeeee;
	padding: 1rem;
	width: 100%;
}

.heading {
	display: inline;
}

.unit {
	color: #999999;
}

.file-upload {
	border: dashed #333333;
	border-radius: 15px;
	color: #979797;
	text-align: center;
}

.hidden {
	display: inline;
}

.browse-files {
	background: transparent;
	border: none;
	color: #697781;
	margin-top: 1rem;
	text-decoration: underline;
}

.selected-file {
	color: #333333;
	font-weight: bold;
	margin: 6rem 0 2rem 0;
}

.toolbar {
	display: flex;
	background: #ffffff;
	justify-content: space-between;
	padding: 0.5rem;
}

.toolbar-section {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.toolbar-radio {
	appearance: none;
}

.label-icon {
	cursor: pointer;
}

.toolbar-radio:checked + .label-icon {
	color: #a7c4f4;
}

.toolbar-select {
	flex-basis: 15rem;
	flex-shrink: 0;
}

.toolbar-button {
	flex-shrink: 0;
}

.grid {
	display: grid;
}
</style>
<style>
.file-preview-thumbnail img {
	max-width: 4rem;
	max-height: 4rem;
}
</style>